import React, { useRef, useState } from 'react';

const LetterCanvas = ({ image, strokes, onComplete }) => {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const currentStroke = useRef([]);
    const [message, setMessage] = useState('');
    const [completedStrokes, setCompletedStrokes] = useState(new Set());

    const getCanvasPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX || e.touches?.[0]?.clientX;
        const clientY = e.clientY || e.touches?.[0]?.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        setDrawing(true);
        currentStroke.current = [getCanvasPos(e)];
    };

    const draw = (e) => {
        if (!drawing) return;
        const ctx = canvasRef.current.getContext('2d');
        const pos = getCanvasPos(e);
        const last = currentStroke.current[currentStroke.current.length - 1];

        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 5;
        ctx.stroke();

        currentStroke.current.push(pos);
    };

    const endDrawing = () => {
        if (!drawing) return;
        setDrawing(false);
        const stroke = currentStroke.current;
        validateStroke(stroke);
    };

    const validateStroke = (userStroke) => {
        const tolerance = 40;
        const angleThreshold = 35;

        const userStart = userStroke[0];
        const userEnd = userStroke[userStroke.length - 1];
        let matchedIndex = -1;

        for (let i = 0; i < strokes.length; i++) {
            if (completedStrokes.has(i)) continue;

            const expected = strokes[i];

            const startDist = Math.hypot(userStart.x - expected.start.x, userStart.y - expected.start.y);
            const endDist = Math.hypot(userEnd.x - expected.end.x, userEnd.y - expected.end.y);

            const userVec = {
                x: userEnd.x - userStart.x,
                y: userEnd.y - userStart.y,
            };
            const expectedVec = {
                x: expected.end.x - expected.start.x,
                y: expected.end.y - expected.start.y,
            };

            const dot = userVec.x * expectedVec.x + userVec.y * expectedVec.y;
            const magA = Math.sqrt(userVec.x ** 2 + userVec.y ** 2);
            const magB = Math.sqrt(expectedVec.x ** 2 + expectedVec.y ** 2);
            const angle = Math.acos(dot / (magA * magB)) * (180 / Math.PI);

            if (startDist < tolerance && endDist < tolerance && angle < angleThreshold) {
                matchedIndex = i;
                break;
            }
        }

        if (matchedIndex >= 0) {
            setCompletedStrokes(prev => {
                const newSet = new Set(prev);
                newSet.add(matchedIndex);

                if (newSet.size === strokes.length) {
                    setMessage('🎉 Correct!');
                    animateCorrectStrokes();
                    onComplete?.();
                } else {
                    setMessage('✅ Good! Keep going...');
                }

                return newSet;
            });
        } else {
            setMessage('❌ Incorrect stroke, try again.');
        }
    };


    const animateCorrectStrokes = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.lineWidth = 5;
        ctx.strokeStyle = 'green';

        let i = 0;

        const drawStroke = (stroke) => {
            let j = 0;
            const drawStep = () => {
                if (j < stroke.length - 1) {
                    ctx.beginPath();
                    ctx.moveTo(stroke[j].x, stroke[j].y);
                    ctx.lineTo(stroke[j + 1].x, stroke[j + 1].y);
                    ctx.stroke();
                    j++;
                    requestAnimationFrame(drawStep);
                } else {
                    i++;
                    if (i < strokes.length) {
                        drawStroke(generateInterpolatedStroke(strokes[i]));
                    }
                }
            };
            drawStep();
        };

        drawStroke(generateInterpolatedStroke(strokes[0]));
    };

    // Generates a linear path from start to end with small steps
    const generateInterpolatedStroke = ({ start, end }) => {
        const points = [];
        const steps = 30;
        for (let i = 0; i <= steps; i++) {
            points.push({
                x: start.x + (end.x - start.x) * (i / steps),
                y: start.y + (end.y - start.y) * (i / steps),
            });
        }
        return points;
    };

    const resetCanvas = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setCompletedStrokes(new Set());
        setMessage('');
    };

    return (
        <>
            <div style={{ position: 'relative', width: 300, height: 300 }}>
                <img src={image} alt="letter" style={{ width: '100%', height: '100%' }} />
                <canvas
                    ref={canvasRef}
                    width={300}
                    height={300}
                    style={{ position: 'absolute', top: 0, left: 0 }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={endDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={endDrawing}
                />
            </div>
            {message && (
                <div style={{ marginTop: 10, fontWeight: 'bold', color: message.includes('Correct') ? 'green' : (message.includes('Good') ? 'blue' : 'red') }}>
                    {message}
                </div>
            )}
            <button onClick={resetCanvas} style={{ marginTop: 10 }}>🔁 Retry</button>
        </>
    );
};

export default LetterCanvas;
